import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { useMatchingRouteKey } from '@/navigation/useRouteKeyMatch';

/**
 * An exhaustive list of all possible waterfall routes.  Note that routes that sit under
 * other routes in this list must still be included, or else they will not match as expected.
 */
const WATERFALL_ROUTES = [
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_OVERVIEW,
  ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS,
];

export const useNavigateToWaterfall = () => {
  const navigate = useNavigate();
  const matchedKey = useMatchingRouteKey(WATERFALL_ROUTES);
  const { clientOrOrgId } = useParams();

  return useCallback(
    ({
      householdId,
      waterfallId,
    }: {
      householdId: string;
      waterfallId?: string | null;
    }) => {
      let path: string;

      if (!waterfallId) {
        // If a waterfall does not exist, go to the base waterfall tab to create a new entity/waterfall
        path = getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB,
          {
            householdId,
          }
        );
      } else if (
        matchedKey ===
        ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS
      ) {
        // special case -- additional URL param is needed for beneficiary reporting individual details
        if (clientOrOrgId) {
          path = getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS,
            {
              householdId,
              waterfallId,
              clientOrOrgId,
            }
          );
        } else {
          path = getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_OVERVIEW,
            {
              householdId,
              waterfallId,
            }
          );
        }
      } else {
        path = getCompletePathFromRouteKey(
          matchedKey ??
            ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY,
          {
            householdId,
            waterfallId,
          }
        );
      }

      navigate(path, {
        replace: !!matchedKey,
      });
    },
    [clientOrOrgId, matchedKey, navigate]
  );
};
